import {
  RenderingEngine,
  Enums as csEnums,
  init as csInit,
  imageLoader,
  cache,
  volumeLoader,
  setVolumesForViewports,
  cornerstoneStreamingImageVolumeLoader,
  eventTarget as csEventTargetForInit,
} from '@cornerstonejs/core';
import * as cornerstoneTools from '@cornerstonejs/tools';
import dicomImageLoader from '@cornerstonejs/dicom-image-loader';
import { getDicomWebAuthHeaders } from '../data/providers';

const {
  ToolGroupManager,
  Enums: csToolsEnums,
  WindowLevelTool,
  PanTool,
  ZoomTool,
  MagnifyTool,
  StackScrollTool,
  ProbeTool,
  LengthTool,
  AngleTool,
  CobbAngleTool,
  RectangleROITool,
  EllipticalROITool,
  ArrowAnnotateTool,
  CrosshairsTool,
  ReferenceLinesTool,
  TrackballRotateTool,
  OrientationMarkerTool,
  ScaleOverlayTool,
  VolumeCroppingTool,
  annotation,
} = cornerstoneTools;

let initialized = false;

export interface SeriesData {
  /** EDGEPACS row id. In DICOMweb mode this is synthesized (a hash of the
   *  UID) so the existing seriesId-keyed UI doesn't need to change. The
   *  actual fetch path uses seriesInstanceUID + sopInstanceUIDs. */
  seriesId: number;
  seriesInstanceUID: string;
  seriesNumber: string;
  seriesDescription: string;
  modality: string;
  numImages: number;
  seriesSize: number;
  bodyPart: string;
  /** Per-instance SOPInstanceUIDs in display order. Populated only in
   *  DICOMweb mode (QIDO /instances response); empty/undefined for
   *  EDGEPACS where the backend serves images by integer index. */
  sopInstanceUIDs?: string[];
  /** Parent StudyInstanceUID — needed by DICOMweb's WADO URL builder,
   *  optional in EDGEPACS mode. */
  studyInstanceUID?: string;
}

export interface StudyData {
  studyId: number;
  studyInstanceUID: string;
  patientName: string;
  patientId: string;
  patientDob: string;
  patientSex: string;
  patientAge: string;
  studyDate: string;
  studyDescription: string;
  accessionNumber: string;
  modality: string;
  referringPhysician: string;
  institution: string;
  numImages: number;
  series: SeriesData[];
}

export const RENDERING_ENGINE_ID = 'edgeweb-rendering-engine';
export const TOOLGROUP_ID = 'edgeweb-toolgroup';
export const TOOLGROUP_3D_ID = 'edgeweb-toolgroup-3d';
export const MPR_VOLUME_PREFIX = 'cornerstoneStreamingImageVolume:edgeweb_mpr_';

export async function initCornerstone(): Promise<void> {
  if (initialized) return;

  // CS3D 5.x handles texture format auto-detection and shader path
  // selection internally. No config needed for our use case — this was
  // a 4.20 workaround where the norm16 probe was broken.
  await csInit();
  await cornerstoneTools.init();

  // ---------------------------------------------------------------------
  // Normalize voiLUTFunction on every loaded image.
  //
  // Cornerstone3D 5.x throws "Invalid VOI LUT function" when
  // `image.voiLUTFunction` doesn't strict-match one of its enum values
  // ("LINEAR", "LINEAR_EXACT", "SIGMOID"). Some modalities (Planmed MG
  // Clarity, various CR/DR) pad the DICOM CS-VR value with a trailing
  // space or NUL to hit the even-length requirement — the raw string is
  // then "LINEAR " or "LINEAR\0". Strict equality fails, StackViewport
  // throws inside its successCallback, and the viewport never renders.
  //
  // IMAGE_LOADED fires from imageLoader.handleImageLoadPromise BEFORE the
  // caller's own .then() runs (Cornerstone attaches its listener first),
  // so patching image.voiLUTFunction here is seen by all downstream
  // consumers including _updateActorToDisplayImageId. Cheap, safe,
  // universal — runs for every image regardless of modality.
  const VOI_LUT_ALLOWED = new Set(['LINEAR', 'LINEAR_EXACT', 'SIGMOID']);
  csEventTargetForInit.addEventListener(
    csEnums.Events.IMAGE_LOADED,
    (e: any) => {
      const img = e?.detail?.image;
      if (!img || typeof img.voiLUTFunction !== 'string') return;
      const raw = img.voiLUTFunction;
      // Trim ASCII whitespace + NULs the DICOM CS-VR spec allows as padding.
      const normalized = raw.replace(/[\s\0]+$/, '').replace(/^[\s\0]+/, '');
      if (normalized === raw && VOI_LUT_ALLOWED.has(raw)) return;   // already fine
      if (VOI_LUT_ALLOWED.has(normalized)) {
        img.voiLUTFunction = normalized;
      } else {
        // Unknown / empty → default to LINEAR (spec-compliant fallback).
        // Includes the empty string case which some datasets emit when
        // the VR is present but zero-length.
        if (raw !== 'LINEAR') {
          console.warn('[voi-normalize] unknown voiLUTFunction; defaulting to LINEAR',
            JSON.stringify(raw));
        }
        img.voiLUTFunction = 'LINEAR';
      }
    }
  );

  dicomImageLoader.init({
    maxWebWorkers: navigator.hardwareConcurrency
      ? Math.min(navigator.hardwareConcurrency, 4)
      : 2,
    // beforeSend lets us inject Authorization (and any other custom
    // headers) into the loader's fetch calls — required for protected
    // DICOMweb servers. The hook is invoked for every image request,
    // including thumbnails, regardless of whether the imageId is
    // wadors: or wadouri:, so we ignore non-wadors URLs to avoid leaking
    // DICOMweb credentials to the EDGEPACS backend.
    beforeSend: (xhr: XMLHttpRequest, imageId: string) => {
      if (!imageId.startsWith('wadors:')) return;
      const headers = getDicomWebAuthHeaders();
      for (const [k, v] of Object.entries(headers)) {
        xhr.setRequestHeader(k, v);
      }
    },
  });

  // Register streaming volume loader for MPR/3D/MIP
  volumeLoader.registerVolumeLoader(
    'cornerstoneStreamingImageVolume',
    cornerstoneStreamingImageVolumeLoader as any
  );

  // Add all tools
  cornerstoneTools.addTool(WindowLevelTool);
  cornerstoneTools.addTool(PanTool);
  cornerstoneTools.addTool(ZoomTool);
  cornerstoneTools.addTool(MagnifyTool);
  cornerstoneTools.addTool(StackScrollTool);
  cornerstoneTools.addTool(ProbeTool);
  cornerstoneTools.addTool(LengthTool);
  cornerstoneTools.addTool(AngleTool);
  cornerstoneTools.addTool(CobbAngleTool);
  cornerstoneTools.addTool(RectangleROITool);
  cornerstoneTools.addTool(EllipticalROITool);
  cornerstoneTools.addTool(ArrowAnnotateTool);
  cornerstoneTools.addTool(CrosshairsTool);
  cornerstoneTools.addTool(ReferenceLinesTool);
  cornerstoneTools.addTool(TrackballRotateTool);
  cornerstoneTools.addTool(OrientationMarkerTool);
  cornerstoneTools.addTool(ScaleOverlayTool);
  cornerstoneTools.addTool(VolumeCroppingTool);

  initialized = true;
}

export function createToolGroup(toolGroupId: string) {
  const existing = ToolGroupManager.getToolGroup(toolGroupId);
  if (existing) return existing;

  const toolGroup = ToolGroupManager.createToolGroup(toolGroupId)!;

  toolGroup.addTool(WindowLevelTool.toolName);
  toolGroup.addTool(PanTool.toolName);
  toolGroup.addTool(ZoomTool.toolName);
  // Magnify needs explicit config — defaults give a tiny invisible glass.
  toolGroup.addTool(MagnifyTool.toolName, {
    magnifyingGlass: { magnifySize: 200, magnificationLevel: 3, radius: 100 },
  });
  toolGroup.addTool(StackScrollTool.toolName);
  toolGroup.addTool(ProbeTool.toolName);
  toolGroup.addTool(LengthTool.toolName);
  toolGroup.addTool(AngleTool.toolName);
  toolGroup.addTool(CobbAngleTool.toolName);
  toolGroup.addTool(RectangleROITool.toolName);
  toolGroup.addTool(EllipticalROITool.toolName);
  toolGroup.addTool(ArrowAnnotateTool.toolName);
  // Crosshairs only does anything in MPR/multi-viewport mode, but it needs
  // to be registered on the toolGroup so the toolbar's Crosshairs button
  // can flip it active without the call silently no-op'ing (which was
  // causing it to fall through to the previous tool — usually Magnify).
  toolGroup.addTool(CrosshairsTool.toolName);
  // ReferenceLines needs a sourceViewportId to actually render anything
  // (which lines to project). We seed it with viewport-0 here; the setup
  // code re-adds this tool with the current active viewport as the
  // source whenever the layout/mode changes.
  toolGroup.addTool(ReferenceLinesTool.toolName, {
    sourceViewportId: 'viewport-0',
  });

  toolGroup.setToolActive(WindowLevelTool.toolName, {
    bindings: [{ mouseButton: csToolsEnums.MouseBindings.Primary }],
  });
  toolGroup.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: csToolsEnums.MouseBindings.Auxiliary }],
  });
  toolGroup.setToolActive(ZoomTool.toolName, {
    bindings: [{ mouseButton: csToolsEnums.MouseBindings.Secondary }],
  });
  toolGroup.setToolActive(StackScrollTool.toolName, {
    bindings: [{ mouseButton: csToolsEnums.MouseBindings.Wheel as any }],
  });
  toolGroup.setToolEnabled(ReferenceLinesTool.toolName);

  // Set annotation tools to Passive so they render existing annotations
  // (Passive = renders + responds to hover, but doesn't create new annotations)
  toolGroup.setToolPassive(LengthTool.toolName);
  toolGroup.setToolPassive(AngleTool.toolName);
  toolGroup.setToolPassive(CobbAngleTool.toolName);
  toolGroup.setToolPassive(RectangleROITool.toolName);
  toolGroup.setToolPassive(EllipticalROITool.toolName);
  toolGroup.setToolPassive(ArrowAnnotateTool.toolName);
  toolGroup.setToolPassive(ProbeTool.toolName);
  // OrientationMarker and ScaleOverlay are enabled after viewports are added
  // (they crash if no viewport exists yet)

  return toolGroup;
}

/** Create a tool group for the 3D viewport with trackball rotation + zoom on wheel */
export function create3DToolGroup() {
  const existing = ToolGroupManager.getToolGroup(TOOLGROUP_3D_ID);
  if (existing) return existing;

  const tg = ToolGroupManager.createToolGroup(TOOLGROUP_3D_ID)!;
  tg.addTool(TrackballRotateTool.toolName);
  tg.addTool(PanTool.toolName);
  tg.addTool(ZoomTool.toolName);
  tg.addTool(VolumeCroppingTool.toolName);

  // Left drag = rotate, right drag = pan, middle + wheel = zoom
  tg.setToolActive(TrackballRotateTool.toolName, {
    bindings: [{ mouseButton: csToolsEnums.MouseBindings.Primary }],
  });
  tg.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: csToolsEnums.MouseBindings.Secondary }],
  });
  tg.setToolActive(ZoomTool.toolName, {
    bindings: [
      { mouseButton: csToolsEnums.MouseBindings.Auxiliary },
      { mouseButton: csToolsEnums.MouseBindings.Wheel as any },
    ],
  });

  return tg;
}

export function destroyToolGroup(toolGroupId: string) {
  const existing = ToolGroupManager.getToolGroup(toolGroupId);
  if (existing) {
    ToolGroupManager.destroyToolGroup(toolGroupId);
  }
}

const { utilities: csToolsUtilities } = cornerstoneTools;

export {
  cornerstoneTools, csToolsUtilities, csEnums,
  RenderingEngine, cache, volumeLoader, setVolumesForViewports,
};
